import { useEffect, useState } from "react";
import { Trash2, ChevronDown, ChevronUp, Mail, Phone, Ruler, Package, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "./AdminFaq";
import ExportCsvButton from "@/components/admin/ExportCsvButton";

const ADDON_LABELS = {
    battery: "Bateria de lítio",
    solar_panel: "Painel solar",
    freezer: "Congelador",
    coffee_machine: "Máquina de café",
    ice_cream_machine: "Máquina de gelados",
    grill: "Grelhador",
    crepe_maker: "Crepeira",
    beer_machine: "Máquina de cerveja",
    pizza_oven: "Forno de pizza",
    hotdog_maker: "Máquina de cachorros",
};

const STATUS_OPTIONS = [
    { value: "new", label: "Novo", className: "bg-amber-100 text-amber-800" },
    { value: "contacted", label: "Contactado", className: "bg-sky-100 text-sky-800" },
    { value: "converted", label: "Convertido", className: "bg-emerald-100 text-emerald-800" },
    { value: "archived", label: "Arquivado", className: "bg-muted text-muted-foreground" },
];

function statusMeta(s) {
    return STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];
}

function formatDate(s) {
    try { return new Date(s).toLocaleString("pt-PT"); } catch { return s; }
}

export default function AdminCartConfigs() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/cart-configurations");
            setItems(data || []);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    async function onDelete(it) {
        if (!window.confirm(`Apagar configuração de ${it.name}?`)) return;
        try {
            await api.delete(`/admin/cart-configurations/${it.id}`);
            toast.success("Apagado");
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro");
        }
    }

    async function onChangeStatus(it, status) {
        try {
            await api.put(`/admin/cart-configurations/${it.id}`, { status });
            setItems((cur) => cur.map((c) => (c.id === it.id ? { ...c, status } : c)));
            toast.success("Estado actualizado");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro");
        }
    }

    return (
        <div data-testid="admin-cart-configs-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <PageHeader
                    title="Configurações do Simulador"
                    subtitle="Pedidos de orçamento submetidos pela página /simulador"
                    count={items.length}
                />
                <ExportCsvButton endpoint="/admin/export/cart-configs" filename="novifood-pedidos-simulador.csv" testId="export-cart-configs-csv" />
            </div>

            <div className="mt-8 space-y-3">
                {loading ? (
                    <div className="p-12 text-center text-muted-foreground rounded-2xl border border-border bg-white">A carregar...</div>
                ) : items.length === 0 ? (
                    <div className="p-12 text-center text-muted-foreground rounded-2xl border border-border bg-white">
                        Sem pedidos de orçamento ainda.
                    </div>
                ) : (
                    items.map((it) => {
                        const open = !!expanded[it.id];
                        const sm = statusMeta(it.status);
                        return (
                            <div key={it.id} data-testid={`config-row-${it.id}`} className="rounded-2xl border border-border bg-white overflow-hidden">
                                <button
                                    onClick={() => setExpanded((p) => ({ ...p, [it.id]: !open }))}
                                    className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-muted/30"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="font-heading font-bold text-primary">{it.name}</span>
                                            <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2.5 py-0.5 ${sm.className}`}>
                                                {sm.label}
                                            </span>
                                            <span className="text-xs text-muted-foreground">{formatDate(it.created_at)}</span>
                                        </div>
                                        <div className="mt-1.5 flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                                            <span className="inline-flex items-center gap-1"><Ruler className="h-3 w-3" /> {it.size}</span>
                                            <span className="inline-flex items-center gap-1"><Package className="h-3 w-3" /> {it.addons?.length || 0} equipamentos</span>
                                            <span className="truncate">{it.email}</span>
                                        </div>
                                    </div>
                                    {open ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
                                </button>

                                {open && (
                                    <div className="px-5 pb-5 border-t border-border space-y-4 pt-4">
                                        <div className="flex flex-wrap gap-4 text-sm">
                                            <a href={`mailto:${it.email}`} className="inline-flex items-center gap-1.5 text-primary hover:underline">
                                                <Mail className="h-4 w-4" /> {it.email}
                                            </a>
                                            {it.phone && (
                                                <a href={`tel:${it.phone}`} className="inline-flex items-center gap-1.5 text-primary hover:underline">
                                                    <Phone className="h-4 w-4" /> {it.phone}
                                                </a>
                                            )}
                                            {it.cidade && (
                                                <span className="inline-flex items-center gap-1.5 text-primary">
                                                    <MapPin className="h-4 w-4" /> {it.cidade}
                                                </span>
                                            )}
                                        </div>

                                        <div>
                                            <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Configuração</div>
                                            <div className="mt-2 grid sm:grid-cols-2 gap-3">
                                                <div className="rounded-lg bg-muted/40 p-3">
                                                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Tamanho</div>
                                                    <div className="mt-1 font-heading font-bold text-primary">{it.size}</div>
                                                </div>
                                                <div className="rounded-lg bg-muted/40 p-3">
                                                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Equipamentos ({it.addons?.length || 0})</div>
                                                    {it.addons?.length ? (
                                                        <ul className="mt-1 flex flex-wrap gap-1.5">
                                                            {it.addons.map((a) => (
                                                                <li key={a} className="text-[11px] rounded-full bg-white border border-border px-2 py-0.5 text-primary">
                                                                    {ADDON_LABELS[a] || a}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <div className="mt-1 text-xs italic text-muted-foreground">Nenhum</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {it.notes && (
                                            <div>
                                                <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Notas</div>
                                                <div className="mt-1 text-sm whitespace-pre-wrap text-muted-foreground bg-muted/30 rounded-lg p-3">
                                                    {it.notes}
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex flex-wrap items-center gap-3 justify-between">
                                            <div className="flex items-center gap-2">
                                                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Estado:</label>
                                                <select
                                                    value={it.status}
                                                    onChange={(e) => onChangeStatus(it, e.target.value)}
                                                    data-testid={`config-status-${it.id}`}
                                                    className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                                >
                                                    {STATUS_OPTIONS.map((o) => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="flex gap-2">
                                                <a
                                                    href={`mailto:${it.email}?subject=${encodeURIComponent("Orçamento NOVIFOOD — Configuração " + it.size)}`}
                                                    className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-xs font-bold"
                                                >
                                                    <Mail className="h-3.5 w-3.5" /> Responder
                                                </a>
                                                <button
                                                    onClick={() => onDelete(it)}
                                                    className="inline-flex items-center gap-2 rounded-md bg-red-50 text-red-700 px-4 py-2 text-xs font-bold hover:bg-red-100"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" /> Apagar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
